import type { PersonaDto } from '@/lib/api/personas-client';

export const mockPersonas: PersonaDto[] = [
  {
    id: 'persona-mock-1',
    type: 'Default',
    creationStatus: 'ready',
    segmentName: 'Eco & Tech Aspirers',
    demographicsData: {
      data: {
        firstName: 'Maya',
        lastName: 'Chen',
        age: '32',
        gender: 'Female',
        city: 'Austin',
        country: 'USA',
        region: 'Texas',
        urbanicity: 'Urban',
        education: "Master's Degree in Computer Science",
        profession: 'Senior Product Manager',
        careerDescription:
          'Maya leads product strategy at a fast-growing tech startup, overseeing a team of designers and engineers. She transitioned from software engineering after 5 years, bringing deep technical understanding to her product decisions.',
        lifestage: 'Young Professional',
        languages: ['English', 'Mandarin'],
        income: '145000',
        incomeCurrency: 'USD',
        homeComposition: 'Lives with partner, no children',
        interests: ['Technology', 'Sustainability', 'Fitness', 'Travel'],
        consumerBehavior: ['Early adopter', 'Research-driven', 'Quality over price'],
      },
      confidence: 'high',
      characteristics: ['Tech-savvy', 'Eco-conscious', 'Career-driven'],
      rationale: {
        locationRationale: 'Austin is a major tech hub with a strong startup ecosystem',
        personalRationale: 'Demographics aligned with tech industry professionals',
        householdRationale: 'Common living situation for young professionals in urban areas',
        behavioralRationale: 'Behaviors consistent with tech industry early adopters',
      },
    },
    socialMediaData: {
      data: {
        platforms: [
          {
            platformName: 'LinkedIn',
            usageLikelihood: 95,
            usageJustification: 'Primary professional networking platform',
            contentConsumption: 'Industry news, thought leadership, job market trends',
            contentCreation: 'Occasional posts about product management insights',
            engagementStyle: 'Professional and thoughtful',
            keyInfluencersOrAccounts: ['Lenny Rachitsky', 'Shreyas Doshi'],
          },
          {
            platformName: 'Twitter/X',
            usageLikelihood: 75,
            usageJustification: 'Tech industry conversations and news',
            contentConsumption: 'Tech news, startup ecosystem updates',
            contentCreation: 'Retweets and occasional commentary',
            engagementStyle: 'Observational with selective engagement',
            keyInfluencersOrAccounts: ['@paulg', '@naval'],
          },
        ],
      },
      confidence: 'high',
      socialMediaUsageSummary:
        'Highly active on professional platforms, uses social media primarily for career development and industry awareness.',
      characteristics: [
        'Professional networker',
        'Industry thought follower',
        'Selective content creator',
      ],
    },
    psychometricsData: {
      data: {
        bigFive: {
          openness: 'High - curious and open to new experiences',
          conscientiousness: 'High - organized and goal-oriented',
          extraversion: 'Moderate - comfortable in social settings but values alone time',
          agreeableness: 'Moderate-High - collaborative but assertive when needed',
          neuroticism: 'Low - emotionally stable and resilient',
        },

        CFC: {
          caresAbout: [
            {
              title: 'Career Growth',
              description: 'Advancing to executive leadership in the tech industry',
            },
            {
              title: 'Work-Life Balance',
              description: 'Maintaining personal wellness while achieving professional goals',
            },
            {
              title: 'Environmental Impact',
              description: 'Supporting sustainable business practices and products',
            },
          ],
          constraintsTradeoffs: {
            budgets: 'Willing to invest in quality products that save time',
            time: 'Limited personal time due to demanding career',
            normalizedTradeOffs: 'Prioritizes convenience and quality over cost savings',
          },
          pains: [
            'Information overload from multiple communication channels',
            'Difficulty disconnecting from work',
            'Finding authentic sustainable products',
          ],
          gains: [
            'Achieving product-market fit milestones',
            'Building high-performing teams',
            'Making environmentally conscious choices',
          ],
          mediaDiet: {
            socialMedia: ['LinkedIn', 'Twitter/X', 'Instagram'],
            newsSources: ['TechCrunch', 'The Information', 'Stratechery'],
            entertainment: ['Documentary podcasts', 'Sci-fi series', 'Travel vlogs'],
          },
          styleProfile: {
            communicationStyle: 'Direct and data-driven',
            aestheticPreferences: 'Clean, minimal, functional design',
          },
          knowledgeState: {
            primaryDomainExpertise: 'Product management and software development',
            technologyFamiliarity: 'Expert - early adopter of new technologies',
            generalKnowledge: 'Well-read across business, technology, and science',
          },
          affectProfile: {
            mood: 'Generally optimistic and energetic',
            attitude: 'Growth-oriented and solution-focused',
          },
          cultureNorms: {
            socialValues: ['Innovation', 'Authenticity', 'Sustainability'],
            lifestyle: ['Urban living', 'Active lifestyle', 'Continuous learning'],
          },
        },
      },
      confidence: 'high',
      characteristics: ['Achievement-oriented', 'Future-focused', 'Environmentally conscious'],
    },
    personaBrief:
      "Maya Chen is a 32-year-old Senior Product Manager at a fast-growing Austin tech startup. With a background in software engineering and a Master's in Computer Science, she brings deep technical expertise to her product decisions. Maya is career-driven and ambitious, aiming for executive leadership while maintaining work-life balance. She's an early adopter of technology, values sustainability, and makes research-driven purchasing decisions. Her social media presence is professional and strategic, primarily using LinkedIn for networking and industry insights.",
    personaQuote:
      '"I believe the best products solve real problems elegantly. When I evaluate a new tool or service, I look for evidence that it will genuinely improve my workflow—not just add another notification to my already-crowded day."',
    photoAssetId: '8e9b5aba-cd2f-404c-94cb-0a0b22f148c8',
    created: new Date().toISOString(),
    updated: new Date().toISOString(),
  },
  {
    id: 'persona-mock-2',
    type: 'Default',
    creationStatus: 'ready',
    segmentName: 'Eco & Tech Aspirers',
    demographicsData: {
      data: {
        firstName: 'Marcus',
        lastName: 'Williams',
        age: '45',
        gender: 'Male',
        city: 'Chicago',
        country: 'USA',
        region: 'Illinois',
        urbanicity: 'Suburban',
        education: 'MBA',
        profession: 'Small Business Owner',
        careerDescription:
          'Marcus owns and operates a chain of three fitness studios in the Chicago suburbs. He built his business from a single location over 12 years, focusing on community and personalized training programs.',
        lifestage: 'Established Professional',
        languages: ['English'],
        income: '180000',
        incomeCurrency: 'USD',
        homeComposition: 'Married with two teenagers',
        interests: ['Fitness', 'Community building', 'Sports', 'Family activities'],
        consumerBehavior: ['Value-conscious', 'Brand loyal', 'Practical'],
      },
      confidence: 'high',
      characteristics: ['Community-focused', 'Family-oriented', 'Entrepreneurial'],
      rationale: {
        locationRationale: 'Chicago suburbs have strong small business community',
        personalRationale: 'Profile matches established small business owners',
        householdRationale: 'Typical family structure for this demographic',
        behavioralRationale: 'Behaviors align with practical, community-focused individuals',
      },
    },
    socialMediaData: {
      data: {
        platforms: [
          {
            platformName: 'Facebook',
            usageLikelihood: 90,
            usageJustification: 'Primary platform for business promotion and community engagement',
            contentConsumption: 'Local community news, business groups, family updates',
            contentCreation: 'Business updates, community events, personal milestones',
            engagementStyle: 'Active and community-oriented',
            keyInfluencersOrAccounts: ['Local business associations', 'Fitness industry pages'],
          },
          {
            platformName: 'YouTube',
            usageLikelihood: 70,
            usageJustification: 'Learning and entertainment',
            contentConsumption: 'Business advice, fitness content, sports highlights',
            contentCreation: 'Occasional workout videos for studio members',
            engagementStyle: 'Primarily consumer, occasional creator',
            keyInfluencersOrAccounts: ['GaryVee', 'Fitness industry channels'],
          },
        ],
      },
      confidence: 'high',
      socialMediaUsageSummary:
        'Uses social media primarily for business promotion and staying connected with local community. Facebook is the primary platform.',
      characteristics: ['Local community networker', 'Business promoter', 'Family connector'],
    },
    psychometricsData: {
      data: {
        bigFive: {
          openness: 'Moderate - open to new ideas within practical bounds',
          conscientiousness: 'High - disciplined and reliable',
          extraversion: 'High - energized by social interaction',
          agreeableness: 'High - values harmony and helping others',
          neuroticism: 'Low - steady and grounded',
        },

        CFC: {
          caresAbout: [
            {
              title: 'Family Security',
              description: 'Providing stability and opportunities for his children',
            },
            {
              title: 'Business Growth',
              description: 'Expanding his fitness studio brand sustainably',
            },
            {
              title: 'Community Impact',
              description: 'Contributing to local community health and wellness',
            },
          ],
          constraintsTradeoffs: {
            budgets: 'Careful with business expenses, invests in proven solutions',
            time: 'Balances business demands with family priorities',
            normalizedTradeOffs: 'Values reliability and proven track record over novelty',
          },
          pains: [
            'Managing multiple locations with limited staff',
            'Keeping up with digital marketing trends',
            'Balancing work and family time',
          ],
          gains: [
            'Seeing members achieve their fitness goals',
            'Building a legacy business for his family',
            'Being recognized in the community',
          ],
          mediaDiet: {
            socialMedia: ['Facebook', 'YouTube', 'Instagram'],
            newsSources: ['Local Chicago news', 'Fox Business', 'Industry publications'],
            entertainment: ['Sports broadcasts', 'Family movies', 'Documentary series'],
          },
          styleProfile: {
            communicationStyle: 'Warm, direct, and motivational',
            aestheticPreferences: 'Classic, professional, approachable',
          },
          knowledgeState: {
            primaryDomainExpertise: 'Fitness training and small business operations',
            technologyFamiliarity: 'Moderate - adopts proven tools',
            generalKnowledge: 'Strong in business and sports, practical knowledge base',
          },
          affectProfile: {
            mood: 'Positive and encouraging',
            attitude: 'Practical optimist, sees challenges as opportunities',
          },
          cultureNorms: {
            socialValues: ['Hard work', 'Family', 'Community service'],
            lifestyle: ['Suburban family life', 'Active and healthy', 'Community involvement'],
          },
        },
      },
      confidence: 'high',
      characteristics: ['Community-builder', 'Family-first', 'Practical entrepreneur'],
    },
    personaBrief:
      "Marcus Williams is a 45-year-old entrepreneur who owns three fitness studios in the Chicago suburbs. Over 12 years, he's built his business through personal relationships and community engagement. With an MBA and hands-on business experience, he balances growth ambitions with family priorities. Marcus is active on Facebook for business promotion and community connection. He values reliability and proven solutions over cutting-edge trends, preferring to adopt technology once it's been validated by peers.",
    personaQuote:
      '"Success in fitness—and in business—comes down to showing up consistently and genuinely caring about the people you serve. I\'d rather have three studios where everyone knows each other\'s names than thirty locations where nobody feels at home."',
    photoAssetId: '8e9b5aba-cd2f-404c-94cb-0a0b22f148c8',
    created: new Date().toISOString(),
    updated: new Date().toISOString(),
  },
  {
    id: 'persona-mock-3',
    type: 'Default',
    creationStatus: 'ready',
    segmentName: 'Eco & Tech Aspirers',
    demographicsData: {
      data: {
        firstName: 'Sofia',
        lastName: 'Rodriguez',
        age: '28',
        gender: 'Female',
        city: 'Miami',
        country: 'USA',
        region: 'Florida',
        urbanicity: 'Urban',
        education: "Bachelor's Degree in Marketing",
        profession: 'Social Media Manager',
        careerDescription:
          'Sofia manages social media strategy for a boutique marketing agency, handling accounts for fashion and lifestyle brands. She started as an intern and quickly rose through the ranks due to her creative content ideas and engagement metrics.',
        lifestage: 'Young Professional',
        languages: ['English', 'Spanish'],
        income: '65000',
        incomeCurrency: 'USD',
        homeComposition: 'Lives alone in apartment',
        interests: ['Fashion', 'Photography', 'Travel', 'Wellness'],
        consumerBehavior: ['Trend-conscious', 'Influenced by social proof', 'Values aesthetics'],
      },
      confidence: 'high',
      characteristics: ['Creative', 'Trend-aware', 'Socially connected'],
      rationale: {
        locationRationale: 'Miami has a vibrant fashion and lifestyle scene',
        personalRationale: 'Profile aligns with young marketing professionals',
        householdRationale: 'Common living situation for young urban professionals',
        behavioralRationale: 'Behaviors typical of social media and marketing professionals',
      },
    },
    socialMediaData: {
      data: {
        platforms: [
          {
            platformName: 'Instagram',
            usageLikelihood: 98,
            usageJustification: 'Core to both professional work and personal brand',
            contentConsumption: 'Fashion, lifestyle, travel, competitor analysis',
            contentCreation: 'Curated lifestyle content, fashion inspiration, travel photography',
            engagementStyle: 'Highly active creator and engager',
            keyInfluencersOrAccounts: ['Fashion brands', 'Travel influencers', 'Design accounts'],
          },
          {
            platformName: 'TikTok',
            usageLikelihood: 85,
            usageJustification: 'Emerging platform for trend discovery and client content',
            contentConsumption: 'Trending content, marketing tips, lifestyle videos',
            contentCreation: 'Behind-the-scenes content, quick tips, trend participation',
            engagementStyle: 'Active creator following trends',
            keyInfluencersOrAccounts: ['Marketing creators', 'Lifestyle influencers'],
          },
        ],
      },
      confidence: 'high',
      socialMediaUsageSummary:
        'Power user across visual platforms. Social media is both her profession and personal passion, with highly curated presence.',
      characteristics: ['Content creator', 'Trend spotter', 'Visual storyteller'],
    },
    psychometricsData: {
      data: {
        bigFive: {
          openness: 'Very High - constantly seeking new experiences and trends',
          conscientiousness: 'Moderate - creative but can be spontaneous',
          extraversion: 'High - thrives on social connection',
          agreeableness: 'High - values relationships and collaboration',
          neuroticism: 'Moderate - sensitive to social feedback',
        },

        CFC: {
          caresAbout: [
            {
              title: 'Creative Expression',
              description: 'Building a personal brand that showcases her creativity',
            },
            {
              title: 'Career Advancement',
              description: 'Growing into a creative director role',
            },
            {
              title: 'Experiences',
              description: 'Traveling and collecting unique experiences',
            },
          ],
          constraintsTradeoffs: {
            budgets: 'Willing to stretch budget for aesthetic and experiential value',
            time: 'Invests significant time in content creation',
            normalizedTradeOffs: 'Prioritizes experiences and aesthetics over practical savings',
          },
          pains: [
            'Algorithm changes affecting content reach',
            'Burnout from constant content creation',
            'Comparison with other creators',
          ],
          gains: [
            'Viral content moments',
            'Growing follower engagement',
            'Recognition from brands she admires',
          ],
          mediaDiet: {
            socialMedia: ['Instagram', 'TikTok', 'Pinterest', 'YouTube'],
            newsSources: ['Social media news', 'Marketing publications', 'Fashion media'],
            entertainment: ['Reality TV', 'Travel content', 'Fashion documentaries'],
          },
          styleProfile: {
            communicationStyle: 'Casual, visual, and emoji-rich',
            aestheticPreferences: 'Trendy, colorful, Instagram-worthy',
          },
          knowledgeState: {
            primaryDomainExpertise: 'Social media marketing and content creation',
            technologyFamiliarity: 'Expert in social platforms and content tools',
            generalKnowledge: 'Strong in pop culture, fashion, and digital trends',
          },
          affectProfile: {
            mood: 'Enthusiastic and expressive',
            attitude: 'Optimistic with FOMO tendencies',
          },
          cultureNorms: {
            socialValues: ['Self-expression', 'Authenticity', 'Connection'],
            lifestyle: ['Urban nightlife', 'Brunch culture', 'Fitness and wellness'],
          },
        },
      },
      confidence: 'high',
      characteristics: ['Trend-driven', 'Experience-seeker', 'Visual-first'],
    },
    personaBrief:
      "Sofia Rodriguez is a 28-year-old Social Media Manager at a Miami boutique agency, specializing in fashion and lifestyle brands. Bilingual in English and Spanish, she brings cultural versatility to her content strategies. Sofia is a power user of Instagram and TikTok, both professionally and personally, with a carefully curated aesthetic presence. She's driven by creative expression and experiences, often prioritizing aesthetic value in her purchasing decisions. Her career goal is to become a creative director.",
    personaQuote:
      '"Every piece of content is an opportunity to tell a story and create a moment. I\'m always asking myself: would I stop scrolling for this? If the answer is no, it\'s back to the drawing board."',
    photoAssetId: '8e9b5aba-cd2f-404c-94cb-0a0b22f148c8',
    created: new Date().toISOString(),
    updated: new Date().toISOString(),
  },
];

export const mockAudienceFlowWidgetData = {
  personas: mockPersonas,
};
